import {
  DYNAMIC_DATE_PRESETS_BACKWARD,
  DYNAMIC_DATE_PRESETS_FORWARD,
  isDynamicFilter,
} from './report-filter-helpers';

const CUSTOM_DYNAMIC_MAX_DAYS = 3650;
const CUSTOM_DYNAMIC_MAX_MONTHS = 120;

export default {
  data() {
    return {
      dateFilterRadioValue: {},
      dynamicDateCustomConfig: {},
      dynamicDateFilterValues: {},
      dynamicDateUnitOptions: [
        { text: 'days', value: 'days' },
        { text: 'months', value: 'months' },
      ],
    };
  },
  methods: {
    normalizeDynamicFilters(dynamicFilters) {
      if (Array.isArray(dynamicFilters)) {
        return dynamicFilters.reduce((result, item) => {
          if (item?.key && isDynamicFilter(item.value)) {
            result[item.key] = item.value;
          }
          return result;
        }, {});
      }

      if (dynamicFilters && typeof dynamicFilters === 'object') {
        return Object.fromEntries(
          Object.entries(dynamicFilters).filter(([, value]) => isDynamicFilter(value))
        );
      }

      return {};
    },
    buildDynamicFiltersPayload() {
      return Object.entries(this.dynamicDateFilterValues || {})
        .filter(([, value]) => isDynamicFilter(value))
        .map(([key, value]) => {
          const filter = this.filters.find(f => f.name === key);
          return {
            key,
            value,
            start_field: filter?.keys?.[0] || null,
            end_field: filter?.keys?.[1] || null,
          };
        });
    },
    getDynamicFilterValue(filter) {
      return this.dynamicDateFilterValues?.[filter.name] || '';
    },
    getDateFilterRadioValue(filter) {
      return this.dateFilterRadioValue[filter.keys[0]] || 'empty';
    },
    setDateFilterRadioValue(filter, value) {
      this.$set(this.dateFilterRadioValue, filter.keys[0], value);
      this.$delete(this.dynamicDateCustomConfig, filter.keys[0]);
      this.$delete(this.dynamicDateFilterValues, filter.name);
      this.$set(this.filterValues, filter.keys[0], '');
      if (filter.keys.length > 1) {
        this.$set(this.filterValues, filter.keys[1], '');
      }
    },
    getDynamicPresetsForFilter(filter) {
      if (filter.dynamic_date_presets) return filter.dynamic_date_presets;
      if (filter.dynamic_date_direction === 'forward') return DYNAMIC_DATE_PRESETS_FORWARD;
      if (filter.dynamic_date_direction === 'backward') return DYNAMIC_DATE_PRESETS_BACKWARD;
      return this.isForwardDynamicFilter(filter) ? DYNAMIC_DATE_PRESETS_FORWARD : DYNAMIC_DATE_PRESETS_BACKWARD;
    },
    isForwardDynamicFilter(filter) {
      if (filter.dynamic_date_direction === 'forward') return true;
      if (filter.dynamic_date_direction === 'backward') return false;
      const name = filter.name?.toLowerCase() || '';
      return name.includes('expire');
    },
    selectDynamicPreset(filter, presetId) {
      const key = filter.keys[0];

      if (presetId === 'dynamic_custom') {
        if (!this.dynamicDateCustomConfig[key]) {
          const defaultUnit = this.isForwardDynamicFilter(filter) ? 'months' : 'days';
          this.$set(this.dynamicDateCustomConfig, key, { value: '', unit: defaultUnit });
        }
        const config = this.dynamicDateCustomConfig[key];
        if (config.value) {
          this.$set(this.dynamicDateFilterValues, filter.name, `custom_${config.value}_${config.unit}`);
        } else {
          this.$delete(this.dynamicDateFilterValues, filter.name);
        }
      } else {
        this.$delete(this.dynamicDateCustomConfig, key);
        this.$set(this.dynamicDateFilterValues, filter.name, presetId);
      }

      this.$set(this.filterValues, key, '');
      if (filter.keys.length > 1) {
        this.$set(this.filterValues, filter.keys[1], '');
      }
    },
    getCustomDynamicValue(filter) {
      const config = this.dynamicDateCustomConfig[filter.keys[0]];
      return config?.value ?? '';
    },
    setCustomDynamicValue(filter, rawValue) {
      const key = filter.keys[0];
      const currentConfig = this.dynamicDateCustomConfig[key] || {
        value: '',
        unit: this.isForwardDynamicFilter(filter) ? 'months' : 'days',
      };

      if (rawValue === '' || rawValue === undefined) {
        this.$set(this.dynamicDateCustomConfig, key, { ...currentConfig, value: '' });
        this.$set(this.filterValues, key, '');
        this.$delete(this.dynamicDateFilterValues, filter.name);
        return;
      }

      const parsedValue = parseInt(rawValue, 10);
      if (!Number.isFinite(parsedValue) || parsedValue < 1) return;
      const max = this.getCustomDynamicMax(filter);
      const value = Math.min(parsedValue, max);

      this.$set(this.dynamicDateCustomConfig, key, { ...currentConfig, value });
      this.$set(this.dynamicDateFilterValues, filter.name, `custom_${value}_${currentConfig.unit}`);
      this.$set(this.filterValues, key, '');
    },
    getCustomDynamicUnit(filter) {
      const config = this.dynamicDateCustomConfig[filter.keys[0]];
      return config?.unit || (this.isForwardDynamicFilter(filter) ? 'months' : 'days');
    },
    getCustomDynamicMax(filter) {
      const unit = this.getCustomDynamicUnit(filter);
      if (unit === 'months') return CUSTOM_DYNAMIC_MAX_MONTHS;
      return CUSTOM_DYNAMIC_MAX_DAYS;
    },
    setCustomDynamicUnit(filter, unit) {
      const key = filter.keys[0];
      const nextUnit = ['days', 'months'].includes(unit) ? unit : 'months';
      const currentConfig = this.dynamicDateCustomConfig[key] || {
        value: '',
        unit: this.isForwardDynamicFilter(filter) ? 'months' : 'days',
      };
      const nextMax = nextUnit === 'months' ? CUSTOM_DYNAMIC_MAX_MONTHS : CUSTOM_DYNAMIC_MAX_DAYS;
      const currentValue = parseInt(currentConfig.value, 10);
      const nextValue = Number.isFinite(currentValue)
        ? Math.min(currentValue, nextMax)
        : '';

      this.$set(this.dynamicDateCustomConfig, key, { ...currentConfig, value: nextValue, unit: nextUnit });
      if (nextValue) {
        this.$set(this.dynamicDateFilterValues, filter.name, `custom_${nextValue}_${nextUnit}`);
        this.$set(this.filterValues, key, '');
      }
    },
  },
};
